import type { MouseEvent } from "react";
import type { TelemetryNote } from "@/types";
import { TimelineNoteMarkers } from "@/components/telemetry/TimelineNoteMarkers";
import {
  formatVideoTimelineClock,
  formatVideoTimelineDuration,
  getVideoTimelineTicks,
} from "@/utils/videoTimeline";
import {
  getVideoSessionClipAtTime,
  type VideoSessionTimeline,
} from "@/utils/videoSessionTimeline";
import { useTranslation } from "react-i18next";

interface VideoOnlyTimelineProps {
  anchorTimestamp: string | null;
  durationSeconds: number | null;
  currentTime: number;
  onTimeChange: (time: number) => void;
  isUsingSessionFallback: boolean;
  timeline?: VideoSessionTimeline | null;
  notes?: TelemetryNote[];
  onNoteClick?: (note: TelemetryNote) => void;
  onNoteCreate?: (timeOffset: number) => void;
  notesEnabled?: boolean;
  /** "card" keeps the standalone centered card; "docked" renders a full-width bar. */
  variant?: "card" | "docked";
}

export default function VideoOnlyTimeline({
  anchorTimestamp,
  durationSeconds,
  currentTime,
  onTimeChange,
  isUsingSessionFallback,
  timeline = null,
  notes = [],
  onNoteClick,
  onNoteCreate,
  notesEnabled = false,
  variant = "card",
}: VideoOnlyTimelineProps) {
  const { t } = useTranslation(["pages", "common"]);
  const resolvedAnchorTimestamp =
    timeline?.anchorTimestamp ?? anchorTimestamp;
  const safeDuration =
    timeline?.range.end ??
    (typeof durationSeconds === "number" && durationSeconds > 0
      ? durationSeconds
      : null);
  const activeClip = getVideoSessionClipAtTime(timeline, currentTime);
  const hasMultipleClips = (timeline?.clips.length ?? 0) > 1;
  const clampedCurrentTime =
    safeDuration === null ? 0 : Math.min(Math.max(currentTime, 0), safeDuration);
  const ticks =
    resolvedAnchorTimestamp && safeDuration !== null
      ? getVideoTimelineTicks(safeDuration)
      : null;
  const timelineTicks = ticks ?? [];

  const handleTrackClick = (event: MouseEvent<HTMLDivElement>) => {
    if (safeDuration === null) {
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(
      Math.max((event.clientX - bounds.left) / bounds.width, 0),
      1,
    );
    const time = ratio * safeDuration;
    if (notesEnabled && onNoteCreate) {
      onNoteCreate(time);
      return;
    }
    onTimeChange(time);
  };

  const fallbackHint = isUsingSessionFallback && (
    <div className="rounded-lg border border-[#3f2b16] bg-[#1b1409] px-3 py-2 text-xs text-[#facc15]">
      {t("pages:sessions.videoTimeline.usingSessionStart")}
    </div>
  );

  const timelineBody = (
    <div>
      <div
        data-testid="video-only-timeline-track"
        className={`relative ${notesEnabled ? "cursor-cell" : "cursor-pointer"}`}
        onClick={handleTrackClick}
        role="presentation"
      >
        <div className="absolute inset-x-0 top-3 h-px bg-[#273244]" />
        {safeDuration !== null && (
          <div
            className="absolute top-0 h-6 w-px bg-[#60a5fa] shadow-[0_0_12px_rgba(96,165,250,0.6)]"
            style={{
              left: `${(clampedCurrentTime / safeDuration) * 100}%`,
            }}
          />
        )}

        <div className="relative h-8">
          {timelineTicks.map((tick) => (
            <div
              key={tick.offsetSeconds}
              className="absolute top-2 -translate-x-1/2"
              style={{ left: `${tick.positionPercent}%` }}
            >
              <div className="h-3 w-px bg-[#475569]" />
              <div className="mt-2 whitespace-nowrap text-xs text-[#64748b]">
                {resolvedAnchorTimestamp
                  ? formatVideoTimelineClock(
                      resolvedAnchorTimestamp,
                      tick.offsetSeconds,
                    )
                  : "--:--:--"}
              </div>
            </div>
          ))}
        </div>

        {safeDuration !== null && notes.length > 0 && (
          <div className="pointer-events-none absolute inset-0">
            <TimelineNoteMarkers
              notes={notes}
              getSessionTime={(note) => note.timeOffset}
              getPointPosition={(time) => `${(time / safeDuration) * 100}%`}
              buildAriaLabel={(note) =>
                t("pages:sessions.videoTimeline.ariaNote", {
                  time: formatVideoTimelineDuration(note.timeOffset),
                  content: note.content,
                })
              }
              onNoteClick={onNoteClick}
            />
          </div>
        )}
      </div>

      {hasMultipleClips && safeDuration !== null && timeline && (
        <div className="relative mt-10 h-12 overflow-hidden rounded-md border border-[#273244] bg-[#080b12]">
          {timeline.clips.map((clip, index) => (
            <div
              key={clip.video.id}
              className="absolute inset-y-1.5 flex items-center overflow-hidden rounded border px-2 text-xs font-medium text-[#dbeafe]"
              style={{
                left: `${(clip.startTime / safeDuration) * 100}%`,
                width: `${(clip.duration / safeDuration) * 100}%`,
                background: index % 2 === 0 ? "#172554" : "#312e81",
                borderColor: index % 2 === 0 ? "#2563eb" : "#8b5cf6",
              }}
            >
              <span className="truncate">{clip.video.filename}</span>
            </div>
          ))}
          <div
            className="pointer-events-none absolute inset-y-0 w-px bg-[#60a5fa] shadow-[0_0_12px_rgba(96,165,250,0.6)]"
            style={{
              left: `${(clampedCurrentTime / safeDuration) * 100}%`,
            }}
          />
        </div>
      )}

      <div className={hasMultipleClips ? "mt-5" : "mt-10"}>
        <input
          type="range"
          min="0"
          max={safeDuration ?? 1}
          step="0.1"
          value={safeDuration === null ? 0 : clampedCurrentTime}
          onChange={(event) => onTimeChange(Number(event.target.value))}
          disabled={safeDuration === null}
          aria-label={t("pages:sessions.videoTimeline.ariaSeek")}
          className="w-full cursor-pointer accent-[#60a5fa] disabled:cursor-not-allowed disabled:opacity-40"
        />
        <div className="mt-2 flex items-center justify-between text-xs text-[#64748b]">
          <span>
            {t("pages:sessions.videoTimeline.start")}{" "}
            {resolvedAnchorTimestamp
              ? formatVideoTimelineClock(resolvedAnchorTimestamp, 0)
              : t("pages:sessions.videoTimeline.unavailable")}
          </span>
          <span>
            {t("pages:sessions.videoTimeline.end")}{" "}
            {resolvedAnchorTimestamp && safeDuration !== null
              ? formatVideoTimelineClock(
                  resolvedAnchorTimestamp,
                  safeDuration,
                )
              : t("pages:sessions.videoTimeline.loadingDuration")}
          </span>
        </div>
      </div>
    </div>
  );

  if (variant === "docked") {
    return (
      <div
        data-testid="video-only-timeline-docked"
        className="flex h-full flex-col justify-end bg-[#080810] px-4 pb-3 pt-2"
      >
        {fallbackHint}
        {resolvedAnchorTimestamp && (
          <div
            data-testid="video-only-timeline-docked-clock"
            className="mb-2 flex items-center justify-end gap-3 text-xs text-[#94a3b8]"
          >
            <span>
              {activeClip
                ? `${activeClip.video.filename} · +${formatVideoTimelineDuration(
                    clampedCurrentTime - activeClip.startTime,
                  )}`
                : `+${formatVideoTimelineDuration(clampedCurrentTime)}`}
            </span>
            <span className="text-[#cbd5e1]">
              {formatVideoTimelineClock(
                resolvedAnchorTimestamp,
                clampedCurrentTime,
              )}
            </span>
          </div>
        )}
        {timelineBody}
      </div>
    );
  }

  return (
    <div className="h-full bg-[#080810] px-6 py-5">
      <div className="mx-auto flex h-full max-w-5xl flex-col justify-center">
        <div className="rounded-2xl border border-[#1a1a2e] bg-[#0d0d14] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.35)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#4b5563]">
                {t("pages:sessions.videoTimeline.title")}
              </p>
              <h2 className="mt-2 text-lg font-semibold text-slate-200">
                {t("pages:sessions.videoTimeline.noTelemetry")}
              </h2>
              <p className="mt-1 text-sm text-[#94a3b8]">
                {t("pages:sessions.videoTimeline.scrubHint")}
              </p>
            </div>
            {resolvedAnchorTimestamp && (
              <div className="text-right text-sm text-[#cbd5e1]">
                <div>
                  {formatVideoTimelineClock(
                    resolvedAnchorTimestamp,
                    clampedCurrentTime,
                  )}
                </div>
                <div className="mt-1 text-xs text-[#64748b]">
                  {activeClip
                    ? `${activeClip.video.filename} · +${formatVideoTimelineDuration(
                        clampedCurrentTime - activeClip.startTime,
                      )}`
                    : `+${formatVideoTimelineDuration(clampedCurrentTime)}`}
                </div>
              </div>
            )}
          </div>

          {fallbackHint && <div className="mt-4">{fallbackHint}</div>}

          <div className="mt-8">{timelineBody}</div>
        </div>
      </div>
    </div>
  );
}
